/*
 *  Copyright (c) Qwoach LLC 2021. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 29/10/2021
 */

import {JournalEntryType} from "@shared/Journal/JournalEntryType";
import {GroupJournalEntry} from "./journalEntry-imports";
import {msToMins} from "@/utils/helpers";
import {LocationItem} from "@shared/scheduler/LocationItem";
import {Type} from "@qwoach/class-transformer-wrapper";
import ScheduledEvent from "@shared/scheduler/ScheduledEvent";
import EventType, {EVENT_TYPE_DEFAULT_ID} from "@shared/scheduler/EventType";
import {InvoiceStatus} from "@shared/Invoice/InvoiceStatus";
import {ServiceType} from "@shared/Quote/ServiceType";
import {toUserLocalTimeFormatted} from "@/utils/timeHelpers";


export default class SessionJournalEntry extends GroupJournalEntry {

	startTimeMs: number = null
	durationMs: number = null

	invoiceStatus: string = InvoiceStatus.NOT_SET
	invoiceStatusChangeTimeMs: number = null
	invoiceId: string = null

	@Type(() => LocationItem)
	location: LocationItem = null;

	skipAutoSchedule: boolean = false
	skipNumbering: boolean = false

	isManuallyScheduled: boolean = false

	sessionNumber: number = null
	bookContentRef: object = null
	sourceServiceId: string = null
	isBillingDisconnected: boolean = false
	isBillingReconciliationRequired: boolean = false

	eventTypeId: string = EVENT_TYPE_DEFAULT_ID
	scheduledEventId: string = null

	serviceType: ServiceType = null

	todoIds: string[] = [] // list of todo item IDs that are linked to this session
	todoTypes: string[] = [] //list of todo types that this session is linked to automatically.

	// we destruct it like this to prevent the type from being overridden
	// eslint-disable-next-line no-unused-vars
	constructor(options = {}) {
		super({...options, type: JournalEntryType.SESSION});
		this.durationMs = options.durationMs || 60 * 60 * 1000; // default to 60 minutes
		this.skipAutoSchedule = options.skipAutoSchedule || false
		this.skipNumbering = options.skipNumbering || false;
		this.sessionNumber = options.sessionNumber || null;
		this.bookContentRef = options.bookContentRef || null;
		this.sourceServiceId = options.sourceServiceId || null;
		this.isBillingDisconnected = options.isBillingDisconnected || false;
		this.isBillingReconciliationRequired = options.isBillingReconciliationRequired || false;
		this.todoIds = options.todoIds || []
		this.todoTypes = options.todoTypes || []
		this.serviceType = ServiceType.SESSION;
	}

	getCurrentSchemaVersion() {
		return 4
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)
		if (oldVersion < 4) {
			if (this.serviceType && this.serviceType !== ServiceType.SESSION) {
				this.legacyServiceType = this.serviceType
			}
			this.serviceType = ServiceType.SESSION
			delete this.sessionType
		}
	}

	startTimeFormatted(format = "ddd, MMM DD YYYY [at] HH:mm") {
		if (!this.startTimeMs) {
			return "";
		}
		return toUserLocalTimeFormatted(this.startTimeMs, format);
		// return dayjs(this.startTimeMs).format(format);
	}

	titleFormatted() {
		let number = this.sessionNumber ? `Session ${this.sessionNumber}` : "";
		let title = String(this.title || "").trim()
		if (number && title) {
			return `${number} - ${title}`;
		}
		if (number) {
			return number;
		}
		if (title) {
			return title;
		}
		return "Session";
	}

	durationInMinutes() {
		return msToMins(this.durationMs)
	}

	durationFormatted(){
		return `${this.durationInMinutes()} mins`;
	}

	addEntry(journalEntry) {

		this.entries.push(journalEntry);
	}

	locationFormatted(){
		if(this.location){
			return this.location.locationFormatted()
		}
		return ""
	}

	// createSessionJournalEntry(){
	// 	let journalEntry = new SessionJournalEntry()
	// 	journalEntry.eventTypeId = this.eventTypeId
	// 	journalEntry.startTimeMs = this.startTime
	// 	journalEntry.durationMs = this.duration
	// 	journalEntry.isManuallyScheduled = true
	// 	journalEntry.location = this.locationFormatted(location)
	// 	journalEntry.title = this.getEventTitle()
	// 	return journalEntry
	// }

	createScheduledEvent(options = {}) {

		let {coachProfile, ownerProfile} = options

		let scheduledEvent = new ScheduledEvent();
		if(EventType.isDefaultId(this.eventTypeId)){
			let eventType = EventType.createDefaultEventType({coachId: coachProfile?.id, ownerId: ownerProfile?.id});
			scheduledEvent.fillFromSessionType(eventType)
		}

		if(this.scheduledEventId){
			console.warn("SessionJournalEntry: createScheduledEvent called on an entry that already has a scheduledEventId. This will overwrite the existing scheduled event ID. Load the existing scheduled event instead.");
			// scheduledEvent.id = this.scheduledEventId;
		}

		scheduledEvent.journalEntryId = this.id

		scheduledEvent.eventTypeId = this.eventTypeId
		scheduledEvent.startTime = this.startTimeMs
		scheduledEvent.duration = this.durationMs
		scheduledEvent.location = this.location
		scheduledEvent.name = this.titleFormatted()

		scheduledEvent.coachId = coachProfile?.id || ownerProfile?.id || null
		scheduledEvent.coachTz = coachProfile?.getTimeZoneId() || scheduledEvent.coachTz || null
		scheduledEvent.ownerId = ownerProfile?.id || coachProfile?.id || null

		scheduledEvent.skipTimeCollisionCheck = true

		return scheduledEvent
	}

	isSessionInvoiced() {
		let nonInvoicedStatuses = [InvoiceStatus.NOT_SET, InvoiceStatus.DRAFT, null]
		return !nonInvoicedStatuses.includes(this.invoiceStatus);
	}

	hasTodoId(todoId){
		return this.todoIds.includes(todoId);
	}
	hasTodoType(todoType){
		return this.todoTypes.includes(todoType);
	}

}
